import React, { createContext, useContext, useState, useEffect } from 'react';
import {
  authAPI, walletAPI,
  getToken, setToken, clearToken,
  getMongoId, setMongoId,
} from '../api/api';

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser]       = useState(null);   // { id, username, email, level }
  const [balance, setBalance] = useState(0);
  const [escrow, setEscrow]   = useState(0);
  const [loading, setLoading] = useState(true);

  // On mount: restore session from saved JWT
  useEffect(() => {
    async function restoreSession() {
      const token = getToken();
      if (!token) { setLoading(false); return; }
      try {
        const profile = await authAPI.me();
        setUser(profile);
        setBalance(profile.balance || 0);
        setEscrow(profile.escrow   || 0);
        if (profile.id) setMongoId(profile.id);
      } catch {
        clearToken();
      } finally {
        setLoading(false);
      }
    }
    restoreSession();
  }, []);

  // Called immediately after every Firebase sign-in / sign-up.
  // Exchanges the Firebase ID token for a ClubSwap JWT, stores it,
  // and returns { user } so callers can update their local state.
  async function loginWithFirebaseToken(idToken) {
    const data = await authAPI.loginWithFirebase(idToken);
    setToken(data.token);
    setMongoId(data.user.id);
    setUser(data.user);
    try {
      const w = await walletAPI.balance();
      setBalance(w.balance);
      setEscrow(w.escrow);
    } catch {}
    return data;
  }

  async function register(username, email, password) {
    const data = await authAPI.register(username, email, password);
    setToken(data.token);
    setMongoId(data.user.id);
    setUser(data.user);
    setBalance(0);
    return data;
  }

  async function login(email, password) {
    const data = await authAPI.login(email, password);
    setToken(data.token);
    setMongoId(data.user.id);
    setUser(data.user);
    try {
      const w = await walletAPI.balance();
      setBalance(w.balance);
      setEscrow(w.escrow);
    } catch {}
    return data;
  }

  function logout() {
    clearToken();
    setUser(null);
    setBalance(0);
    setEscrow(0);
  }

  async function refreshBalance() {
    try {
      const w = await walletAPI.balance();
      setBalance(w.balance);
      setEscrow(w.escrow);
    } catch (err) {
      console.error('Failed to refresh balance:', err.message);
    }
  }

  const value = {
    user,
    balance,
    escrow,
    loading,
    isLoggedIn: !!user,
    loginWithFirebaseToken,
    register,
    login,
    logout,
    refreshBalance,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used inside AuthProvider');
  return ctx;
}
